import { useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { LoaderCircle, LogIn, LogOut, Moon, Sun, UserRound } from "lucide-react";
import {
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import { removeAuthenticatedFromCache } from "@/lib/queryClient";
import { revokeAllThumbnails } from "@/features/coach-review/hooks/useThumbnail";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { Logo } from "@/components/brand/Logo";
import { getPublicNavCapabilities } from "./navCapabilities";
import { LOCKDOWN_MODE } from "./lockdown";
import {
  getInitialTheme,
  setTheme,
  type ThemePreference,
} from "./themePreference";
import { useBrandSync } from "./useBrandSync";
import { cn } from "@/lib/cn";

export function getPublicHeaderLoginNext(pathname: string, fullPath = pathname): string {
  const routePath = pathname || "/";
  if (
    routePath === "/" ||
    /^\/(?:login|reset-password|verify-email)(?:\/|$)/.test(routePath)
  ) {
    return "/hub";
  }
  return fullPath || routePath;
}

/** Shared app header rendered at the root layout so every route gets
 * the same nav + auth boundary + theme toggle.
 *
 * Product branding (mark + wordmark) is bundled with the app — see
 * `web/public/brand/`. The product name `app_name` ("tktic") is
 * fixed and is NOT operator-configurable — the inline wordmark
 * always says "tktic", and the backend rejects PUTs against the
 * `app_name` settings key (see `tests/test_settings.py::
 * test_app_name_is_not_writable`).
 *
 * Operator branding is a separate concern: `season_title` (the
 * club title) and the uploaded `favicon_url` flow into the browser
 * tab (title + favicon) via `useBrandSync` below. That's about the
 * operator's club identity on the tab strip, not the product name.
 *
 * Public pages stay anonymous-readable; signed-in users see the
 * role-gated Coach/My Feedback/Admin links.
 */
export function PublicAppHeader() {
  useMe();
  useBrandSync();
  const authMe = useAuthStore((s) => s.me);
  const location = useLocation();
  const navigate = useNavigate();
  const applyAnon = useAuthStore((s) => s.applyAnonymous);
  const clearScope = useScopeStore((s) => s.clear);
  const [theme, setThemeState] = useState<ThemePreference>(() => getInitialTheme());
  const [signOutPending, setSignOutPending] = useState(false);

  const caps = getPublicNavCapabilities(authMe);
  const currentPath =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}` || "/"
      : location.pathname || "/";
  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname || "/" : location.pathname || "/";
  const loginNext = getPublicHeaderLoginNext(currentPathname, currentPath);
  const navLinkClass = (active: boolean) =>
    cn(
      "rounded border px-2.5 py-1 transition-colors",
      active
        ? "border-accent bg-accent text-accent-ink"
        : "border-rule text-ink-muted hover:border-accent hover:bg-field hover:text-ink",
    );
  const iconControlClass = (tone: "neutral" | "destructive" | "accent" = "neutral") =>
    cn(
      "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50",
      tone === "accent"
        ? "bg-accent text-accent-ink hover:bg-accent-hover"
        : tone === "destructive"
          ? "text-destructive hover:bg-destructive/10"
          : "text-ink-muted hover:bg-field hover:text-ink",
    );
  const isTeamsActive =
    currentPathname.startsWith("/teams") || currentPathname.startsWith("/match/");
  const isCoachActive = currentPathname.startsWith("/coach");
  const isFeedbackActive = currentPathname.startsWith("/feedback");
  const isAdminActive = currentPathname.startsWith("/admin");

  const toggleTheme = () => {
    setThemeState((prev) => setTheme(prev === "dark" ? "light" : "dark"));
  };

  const themeToggle = (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="public-header-theme-toggle"
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className={iconControlClass()}
    >
      {theme === "dark" ? (
        <Sun size={16} strokeWidth={2} aria-hidden="true" />
      ) : (
        <Moon size={16} strokeWidth={2} aria-hidden="true" />
      )}
    </button>
  );

  const signOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        await apiLogout();
      } catch {
        // Server-side session may already be gone; local cleanup is authoritative.
      }
      setAuthToken(null);
      applyAnon();
      clearScope();
      removeAuthenticatedFromCache();
      revokeAllThumbnails();
      void navigate({ to: "/", replace: true });
    } finally {
      setSignOutPending(false);
    }
  };

  const adminLink = caps.canOpenAdmin ? (
    <Link
      to="/admin"
      data-testid="public-header-admin-link"
      className={navLinkClass(isAdminActive)}
    >
      Admin
    </Link>
  ) : null;

  const authActions = caps.signedIn ? (
    <>
      <Link
        to="/me"
        data-testid="public-header-account-link"
        aria-label={`Account: ${caps.displayName || "signed in user"}`}
        title={caps.displayName || "Account"}
        className={iconControlClass()}
      >
        <UserRound size={16} strokeWidth={2} aria-hidden="true" />
      </Link>
      <button
        type="button"
        onClick={() => void signOut()}
        disabled={signOutPending}
        data-testid="public-header-logout"
        aria-label={signOutPending ? "Signing out" : "Log out"}
        title={signOutPending ? "Signing out" : "Log out"}
        className={iconControlClass("destructive")}
      >
        {signOutPending ? (
          <LoaderCircle size={16} strokeWidth={2} aria-hidden="true" className="animate-spin" />
        ) : (
          <LogOut size={16} strokeWidth={2} aria-hidden="true" />
        )}
      </button>
    </>
  ) : (
    <Link
      to="/login"
      search={{ next: loginNext }}
      data-testid="public-header-login-link"
      aria-label="Log in"
      title="Log in"
      className={iconControlClass("accent")}
    >
      <LogIn size={16} strokeWidth={2} aria-hidden="true" />
    </Link>
  );

  // Lockdown mode: the public site exposes only the team pages, so
  // the header drops the Teams/Coach/Feedback nav. It still surfaces
  // the theme toggle (a viewer preference) and an auth path: an
  // anonymous visitor sees Login; a signed-in admin sees the Admin
  // link plus Account/Logout so they can reach backend management.
  // `/login` + `/admin` are reachable here (see lockdown.ts) and
  // `AdminShell` runs its own auth guard, so this exposes no surface
  // an unauthenticated user couldn't already reach by URL.
  if (LOCKDOWN_MODE) {
    return (
      <header
        data-testid="public-app-header"
        className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <span
            className="flex items-center text-ink"
            data-testid="public-header-brand"
            aria-label="tktic"
          >
            <Logo size={28} data-testid="public-header-app-logo" />
          </span>
          <div className="flex min-w-0 items-center gap-2 overflow-x-auto px-1 py-1 text-[0.68rem] font-display uppercase tracking-wider sm:overflow-visible">
            {themeToggle}
            {adminLink}
            {authActions}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="public-app-header"
      className="sticky top-0 z-50 border-b border-rule bg-surface/95 text-ink backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <Link
            to="/"
            data-testid="public-header-brand"
            aria-label="tktic — home"
            className="flex items-center text-ink hover:text-accent transition-colors"
          >
            <Logo
              size={28}
              data-testid="public-header-app-logo"
            />
          </Link>
          <nav
            aria-label="Public navigation"
            className="-mx-1 flex min-w-0 items-center gap-2 overflow-x-auto px-1 pb-0.5 text-[0.68rem] font-display uppercase tracking-wider sm:mx-0 sm:px-0"
          >
            <Link
              to="/teams"
              data-testid="public-header-teams-link"
              className={navLinkClass(isTeamsActive)}
            >
              Teams
            </Link>
            {caps.canOpenCoach ? (
              <Link
                to="/coach"
                data-testid="public-header-coach-link"
                className={navLinkClass(isCoachActive)}
              >
                Coach
              </Link>
            ) : null}
            {caps.canOpenFeedback ? (
              <Link
                to="/feedback"
                data-testid="public-header-feedback-link"
                className={navLinkClass(isFeedbackActive)}
              >
                My Feedback
              </Link>
            ) : null}
            {adminLink}
          </nav>
        </div>

        <div className="-mx-1 flex min-w-0 items-center gap-2 overflow-x-auto px-1 py-1 text-[0.68rem] font-display uppercase tracking-wider sm:mx-0 sm:overflow-visible">
          {themeToggle}
          {authActions}
        </div>
      </div>
    </header>
  );
}
